import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../store/useAuthStore';

export default function SSOLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((state) => state.login);
  const token = useAuthStore((state) => state.token);

  const [status, setStatus] = useState('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetail, setErrorDetail] = useState('');

  useEffect(() => {
    if (token) {
      navigate('/dashboard', { replace: true });
      return;
    }

    const ssoToken = searchParams.get('token');

    if (!ssoToken) {
      setStatus('error');
      setErrorMessage('No SSO token found in URL.');
      setErrorDetail('Please log in from your clinic portal.');
      return;
    }

    async function verifySsoToken() {
      try {
        const { data } = await api.post('/auth/sso-verify', { token: ssoToken });
        login(data.token, data.user);
        navigate(data.user?.setupCompleted ? '/dashboard' : '/setup', { replace: true });
      } catch (err) {
        const statusCode = err?.response?.status;
        const errorData = err?.response?.data;
        setStatus('error');

        if (statusCode === 401) {
          setErrorMessage('Your login link has expired or is invalid.');
          setErrorDetail('Please return to your clinic portal and log in again.');
        } else if (statusCode === 403) {
          setErrorMessage('Access denied.');
          setErrorDetail(errorData?.error || 'Please contact support.');
        } else if (statusCode === 404) {
          setErrorMessage('Account not found.');
          setErrorDetail('No clinic account exists for this login.');
        } else {
          setErrorMessage('Something went wrong while signing you in.');
          setErrorDetail(errorData?.error || 'Please try again or contact support.');
        }
      }
    }

    verifySsoToken();
  }, [searchParams, navigate, login, token]);

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl bg-white shadow-xl p-8 text-center">
          <div className="mb-6">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-500">Clinic OS</div>
            <h1 className="mt-3 text-3xl font-bold text-slate-900">Dektal 360 Pro</h1>
          </div>
          <div className="flex justify-center mb-6">
            <div className="h-12 w-12 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin" />
          </div>
          <p className="text-slate-600 font-medium">Signing you in...</p>
          <p className="mt-2 text-sm text-slate-400">Verifying your session from the clinic portal</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-xl p-8">
        <div className="mb-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-500">Clinic OS</div>
          <h1 className="mt-3 text-3xl font-bold text-slate-900">Dektal 360 Pro</h1>
        </div>

        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-5 mb-6">
          <p className="font-semibold text-rose-700">{errorMessage}</p>
          {errorDetail && <p className="mt-1 text-sm text-rose-600">{errorDetail}</p>}
        </div>

        <div className="space-y-3">
          <a
            href={process.env.REACT_APP_PORTAL_URL || '/'}
            className="block w-full rounded-2xl bg-indigo-600 px-4 py-3 text-center text-white font-semibold hover:bg-indigo-700 transition-colors"
          >
            Return to Portal
          </a>

          {process.env.REACT_APP_SHOW_INTERNAL_LOGIN === 'true' && (
            <button
              onClick={() => navigate('/login')}
              className="block w-full rounded-2xl border border-slate-300 px-4 py-3 text-center text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            >
              Use Internal Login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}